import React, { useState } from 'react';
import { RefreshCwIcon, SettingsIcon } from 'lucide-react';
import { useLeadData } from '../hooks/useLeadData';
import { MetricsBar } from '../components/MetricsBar';
import { SourceHealthGrid } from '../components/SourceHealthGrid';
import { UnresolvedLeadsTable } from '../components/UnresolvedLeadsTable';
import { AllLeadsTable } from '../components/AllLeadsTable';
import { PushConfirmationDialog } from '../components/PushConfirmationDialog';
import { PendingVerification } from '../components/PendingVerification';
import { RecentlyResolved } from '../components/RecentlyResolved';
export function CommandCenter() {
  const {
    leads,
    unresolvedLeads,
    pendingVerificationLeads,
    resolvedLeads,
    metrics,
    sourceHealth,
    selectedLeadIds,
    searchQuery,
    setSearchQuery,
    sourceFilter,
    setSourceFilter,
    statusFilter,
    setStatusFilter,
    toggleSelect,
    selectAll,
    pushSelectedToList,
    pushSingleLead,
    undoResolve,
    refreshData
  } = useLeadData();
  const [activeTab, setActiveTab] = useState<'routing-issues' | 'all-leads'>('routing-issues');
  const [isPushDialogOpen, setIsPushDialogOpen] = useState(false);
  const [singlePushId, setSinglePushId] = useState<string | null>(null);
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const availableSources = sourceHealth.map(s => s.name);
  const handlePushSelected = () => {
    setSinglePushId(null);
    setIsPushDialogOpen(true);
  };
  const handlePushSingle = (id: string) => {
    setSinglePushId(id);
    setIsPushDialogOpen(true);
  };
  const confirmPush = () => {
    if (singlePushId) {
      pushSingleLead(singlePushId);
    } else {
      pushSelectedToList();
    }
  };
  // Get leads to show in the dialog
  const leadsToPush = singlePushId ? leads.filter(l => l.id === singlePushId) : leads.filter(l => selectedLeadIds.has(l.id));
  return <div className="min-h-screen bg-gray-50 pb-12" data-id="element-389">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-390">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-391">
          <div className="flex justify-between items-center h-16" data-id="element-392">
            <div data-id="element-393">
              <h1 className="text-2xl font-bold text-gray-900 leading-tight" data-id="element-394">
                Five9 Routing Command Center
              </h1>
              <p className="text-sm text-gray-500" data-id="element-395">{today}</p>
            </div>
            <div className="flex items-center space-x-4" data-id="element-396">
              <span className="text-xs text-gray-400 hidden sm:inline-block" data-id="element-397">
                Last synced:{' '}
                {new Date().toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
              </span>
              <button onClick={refreshData} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-398">
                <RefreshCwIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-399" />
                Refresh Data
              </button>
              <button onClick={() => {}} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-400">
                <SettingsIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-401" />
                Five9 Settings
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-8" data-id="element-402">
        <section aria-label="Key Metrics" data-id="element-403">
          <MetricsBar metrics={metrics} data-id="element-404" />
        </section>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200" data-id="element-405">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs" data-id="element-406">
            <button onClick={() => setActiveTab('routing-issues')} className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'routing-issues' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `} data-id="element-407">
              Routing Issues
              {unresolvedLeads.length > 0 && <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${activeTab === 'routing-issues' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-900'}`} data-id="element-408">
                  {unresolvedLeads.length}
                </span>}
            </button>
            <button onClick={() => setActiveTab('all-leads')} className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'all-leads' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `} data-id="element-409">
              All Leads Today
              <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${activeTab === 'all-leads' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-900'}`} data-id="element-410">
                {leads.length}
              </span>
            </button>
          </nav>
        </div>

        {activeTab === 'routing-issues' ? <>
            <section aria-label="Source Health" data-id="element-411">
              <SourceHealthGrid sourceHealth={sourceHealth} activeSourceFilter={sourceFilter} onSourceClick={source => setSourceFilter(source === sourceFilter ? null : source)} data-id="element-412" />
            </section>

            <section aria-label="Unresolved Leads" data-id="element-413">
              <UnresolvedLeadsTable leads={unresolvedLeads} selectedIds={selectedLeadIds} onToggleSelect={toggleSelect} onSelectAll={selectAll} searchQuery={searchQuery} onSearchChange={setSearchQuery} sourceFilter={sourceFilter} onSourceFilterChange={setSourceFilter} statusFilter={statusFilter} onStatusFilterChange={setStatusFilter} onPushSelected={handlePushSelected} onPushSingle={handlePushSingle} availableSources={availableSources} data-id="element-414" />
            </section>

            <section aria-label="Pending Verification" data-id="element-415">
              <PendingVerification leads={pendingVerificationLeads} onUndo={undoResolve} data-id="element-416" />
            </section>

            <section aria-label="Verified Leads" data-id="element-417">
              <RecentlyResolved resolvedLeads={resolvedLeads} onUndo={undoResolve} data-id="element-418" />
            </section>
          </> : <section aria-label="All Leads" data-id="element-419">
            <AllLeadsTable leads={leads} availableSources={availableSources} onPushSingle={handlePushSingle} data-id="element-420" />
          </section>}
      </main>

      {/* Modals */}
      <PushConfirmationDialog isOpen={isPushDialogOpen} onClose={() => setIsPushDialogOpen(false)} onConfirm={confirmPush} selectedLeads={leadsToPush} data-id="element-421" />
    </div>;
}